import { useEffect, useMemo, useState } from 'react';
import { getMarkerHistory, type MarkerPoint } from '../api.js';
import { EmptyState, Sheet } from '../ui/index.js';

interface Props {
  marker: string;
  label: string;
  onClose: () => void;
}

/** Format a YYYY-MM-DD as a short date. */
function shortDate(d: string): string {
  const parsed = new Date(`${d}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? d
    : parsed.toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: 'numeric' });
}

/**
 * A modal charting one marker's history: a line trace over a shaded reference
 * band (from the most recent point's refLow/refHigh), plus a value table.
 */
export default function MarkerHistoryModal({ marker, label, onClose }: Props): JSX.Element {
  const [points, setPoints] = useState<MarkerPoint[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getMarkerHistory(marker)
      .then((res) => alive && setPoints(res.points))
      .catch((err: unknown) => alive && setError(err instanceof Error ? err.message : 'Failed to load.'));
    return () => {
      alive = false;
    };
  }, [marker]);

  const chart = useMemo(() => {
    const values = (points ?? []).filter((p) => p.value !== null) as (MarkerPoint & { value: number })[];
    if (values.length === 0) return null;

    const w = 460;
    const h = 160;
    const pad = 12;
    const ref = [...values].reverse().find((p) => p.refLow !== null || p.refHigh !== null);
    const nums = values.map((p) => p.value);
    const lo = Math.min(...nums, ref?.refLow ?? Infinity);
    const hi = Math.max(...nums, ref?.refHigh ?? -Infinity);
    const min = Number.isFinite(lo) ? lo : Math.min(...nums);
    const max = Number.isFinite(hi) ? hi : Math.max(...nums);
    const span = max - min || 1;
    const x = (i: number): number => (values.length === 1 ? w / 2 : pad + (i / (values.length - 1)) * (w - pad * 2));
    const y = (v: number): number => h - pad - ((v - min) / span) * (h - pad * 2);

    const line = values.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
    const band =
      ref && (ref.refLow !== null || ref.refHigh !== null)
        ? { top: y(ref.refHigh ?? max), bottom: y(ref.refLow ?? min) }
        : null;

    return { w, h, pad, values, x, y, line, band, ref };
  }, [points]);

  const latest = points && points.length > 0 ? points[points.length - 1] : null;
  return (
    <Sheet title={label} onClose={onClose} className="bc-marker">
      {error && (
        <p className="bc-banner" role="alert">
          {error}
        </p>
      )}
      {!points && !error && <div className="skeleton bc-skel bc-skel--chart" />}

      {latest && (
        <div className="bc-marker__latest">
          <span className="bc-marker__value num">
            {latest.value ?? '—'}
            {latest.unit && <span className="bc-marker__unit"> {latest.unit}</span>}
          </span>
          <span className="bc-muted">Latest · {shortDate(latest.date)}</span>
        </div>
      )}

      {points && points.length > 0 && chart && (
        <div className="bc-marker__chart">
          <svg className="markerchart" viewBox={`0 0 ${chart.w} ${chart.h}`} preserveAspectRatio="none" role="img" aria-label={`${label} over time`}>
            {chart.band && (
              <rect
                x={0}
                y={Math.min(chart.band.top, chart.band.bottom)}
                width={chart.w}
                height={Math.abs(chart.band.bottom - chart.band.top)}
                fill="var(--good)"
                opacity={0.12}
                rx={6}
              />
            )}
            <path d={chart.line} fill="none" stroke="var(--accent)" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            {chart.values.map((p, i) => (
              <circle key={i} cx={chart.x(i)} cy={chart.y(p.value)} r={3.5} fill="var(--accent)" />
            ))}
          </svg>
          {chart.ref && (chart.ref.refLow !== null || chart.ref.refHigh !== null) && (
            <p className="bc-marker__ref">
              <span className="bc-marker__band" aria-hidden="true" />
              Reference {chart.ref.refLow ?? '—'}–{chart.ref.refHigh ?? '—'} {chart.ref.unit ?? ''}
            </p>
          )}
        </div>
      )}

      {points && points.length === 0 && <EmptyState icon="trend" title="No values yet" />}

      {points && points.length > 0 && (
        <ul className="ui-group bc-inset" aria-label="History">
          {[...points].reverse().map((p, i) => {
            const out = p.value !== null && ((p.refLow !== null && p.value < p.refLow) || (p.refHigh !== null && p.value > p.refHigh));
            return (
              <li key={i} className="ui-swipe">
                <div className="ui-row">
                  <span className="ui-row__main">
                    <span className="ui-row__text">
                      <span className="ui-row__title">{shortDate(p.date)}</span>
                      {(p.refLow !== null || p.refHigh !== null) && (
                        <span className="ui-row__sub">
                          Range {p.refLow ?? '—'}–{p.refHigh ?? '—'}
                        </span>
                      )}
                    </span>
                    <span className={`ui-row__detail num${out ? ' bc-warn' : ''}`}>
                      {p.value ?? '—'} {p.unit ?? ''}
                    </span>
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Sheet>
  );
}
